/*
 * useSeo — imperatively updates document.title + relevant meta/link tags for
 * the current page. Because the app is a React SPA there is no server-side
 * render, but modern crawlers (Google, Bing, DuckDuckGo) execute JS and read
 * the DOM after mount — so setting meta tags this way DOES get indexed.
 *
 * Usage:
 *   useSeo({
 *     title: "Haber Başlığı - SİNE81",
 *     description: "Kısa açıklama",
 *     keywords: "haber, düzce, gündem",
 *     canonical: "https://sine81.com/haber/slug",
 *     image: "https://.../cover.jpg",
 *     noindex: false,
 *     type: "article",
 *     publishedAt, modifiedAt, author, section,
 *   })
 */
import { useEffect } from "react";

const DEFAULT_TITLE = "SİNE81 — 81 İlden Tek Gerçek";

// Track which tags we added so we can safely remove them on unmount.
const OWNED_ATTR = "data-seo-owner";

function upsertMeta({ name, property, content }) {
  if (content === undefined || content === null) return;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  const isNew = !el;
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
  return { el, isNew };
}

function upsertLink({ rel, href }) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const isNew = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, isNew };
}

function setJsonLd(json) {
  if (!json) return null;
  let el = document.head.querySelector('script[type="application/ld+json"][data-seo-owner]');
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute(OWNED_ATTR, "1");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
  return el;
}

export default function useSeo(opts) {
  useEffect(() => {
    if (!opts) return;
    const previousTitle = document.title;
    if (opts.title) document.title = opts.title;

    const created = [];
    const push = (r) => { if (r?.isNew) created.push(r.el); };

    push(upsertMeta({ name: "description", content: opts.description }));
    push(upsertMeta({ name: "keywords", content: opts.keywords }));
    push(upsertMeta({
      name: "robots",
      content: opts.noindex ? "noindex,follow" : "index,follow,max-image-preview:large",
    }));

    // OpenGraph
    push(upsertMeta({ property: "og:type", content: opts.type || "article" }));
    push(upsertMeta({ property: "og:title", content: opts.title }));
    push(upsertMeta({ property: "og:description", content: opts.description }));
    push(upsertMeta({ property: "og:url", content: opts.canonical }));
    push(upsertMeta({ property: "og:image", content: opts.image }));
    push(upsertMeta({ property: "og:site_name", content: "SİNE81 Haber | TV" }));
    push(upsertMeta({ property: "og:locale", content: "tr_TR" }));

    // Twitter
    push(upsertMeta({ name: "twitter:card", content: "summary_large_image" }));
    push(upsertMeta({ name: "twitter:title", content: opts.title }));
    push(upsertMeta({ name: "twitter:description", content: opts.description }));
    push(upsertMeta({ name: "twitter:image", content: opts.image }));

    // Article-specific
    if (opts.publishedAt) push(upsertMeta({ property: "article:published_time", content: opts.publishedAt }));
    if (opts.modifiedAt) push(upsertMeta({ property: "article:modified_time", content: opts.modifiedAt }));
    if (opts.author) push(upsertMeta({ property: "article:author", content: opts.author }));
    if (opts.section) push(upsertMeta({ property: "article:section", content: opts.section }));

    // Canonical link
    push(upsertLink({ rel: "canonical", href: opts.canonical }));

    // JSON-LD structured data
    let ldEl = null;
    if (opts.type === "article" || !opts.type) {
      ldEl = setJsonLd({
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: opts.title,
        description: opts.description,
        image: opts.image ? [opts.image] : undefined,
        datePublished: opts.publishedAt,
        dateModified: opts.modifiedAt || opts.publishedAt,
        author: opts.author ? { "@type": "Person", name: opts.author } : undefined,
        publisher: {
          "@type": "Organization",
          name: "SİNE81",
          logo: {
            "@type": "ImageObject",
            url: (opts.origin || "") + "/api/static/brand/logo_primary.png",
          },
        },
        mainEntityOfPage: opts.canonical,
        articleSection: opts.section,
        keywords: opts.keywords,
      });
    }

    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      created.forEach((el) => el.remove());
      // Clear content of any lingering owned meta tags but don't remove
      // structural ones like description that might have been pre-existing.
      if (ldEl) ldEl.remove();
    };
  }, [
    opts?.title, opts?.description, opts?.keywords, opts?.canonical,
    opts?.image, opts?.noindex, opts?.type, opts?.publishedAt,
    opts?.modifiedAt, opts?.author, opts?.section, opts?.origin,
  ]);
}
